import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { logActivity } from "./lib/activity";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Engagement beacon for signed-in dashboard pages (assurance has its own
    // richer view-level beacon; public pages are not tracked).
    const tracked = ["/dashboard", "/guide", "/upload", "/billing", "/settings", "/subscription-management"];
    if (tracked.some((route) => pathname === route || pathname.startsWith(route + "/"))) {
      logActivity("page_view", { path: pathname });
    }
  }, [pathname]);
  return null;
}

import Dashboard from "./pages/Dashboard";
import ReposList from "./pages/ReposList";
import RepoDetails from "./pages/RepoDetails";
import Onboarding from "./pages/Onboarding";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Join from "./pages/Join";
import UploadCode from "./pages/UploadCode";
import Guide from "./pages/Guide";
import ApiDocs from "./pages/ApiDocs";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import StatusPage from "./pages/StatusPage";
import Documentation from "./pages/Documentation";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";
import CookiePolicy from "./pages/CookiePolicy";
import Brochure from "./pages/Brochure";
import Inquiry from "./pages/Inquiry";
import CodeTrust from "./pages/CodeTrust";
import WhyPaxley from "./pages/WhyPaxley";
import { InsightsIndex, InsightArticle } from "./pages/Insights";
import Security from "./pages/Security";
import AssuranceWorkspace from "./pages/AssuranceWorkspace";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

export default function App() {
  // Apply light mode by default
  useEffect(() => {
    const savedTheme = localStorage.getItem("paxley-theme");
    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
  }, []);

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
          staleTime: 30000, // 30 seconds
        },
      },
    });
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="h-10 w-10 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted mt-4 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const location = useLocation();

  if (location.pathname === "/demo/assurance") {
    return (
      <Routes>
        <Route path="/demo/assurance" element={<AssuranceWorkspace demo />} />
      </Routes>
    );
  }

  // Invite redemption. Deliberately outside ProtectedRoute: an invitee arrives
  // signed out, and the page itself parks the token and routes them through
  // sign-in. Under ProtectedRoute the redirect to /login dropped the token and
  // the invitation was lost.
  if (location.pathname === "/join") {
    return (
      <Routes>
        <Route path="/join" element={<Join />} />
      </Routes>
    );
  }

  // Public routes (no auth required)
  const publicRoutes = ["/", "/login", "/pricing", "/security", "/status", "/docs", "/terms", "/privacy", "/refund", "/cookies", "/brochure", "/inquiry", "/code-trust", "/why-paxley", "/insights"];
  const isPublicRoute =
    publicRoutes.includes(location.pathname) || location.pathname.startsWith("/insights/");

  // Full page routes (no layout)
  const fullPageRoutes = ["/", "/login", "/pricing", "/security", "/setup", "/onboarding", "/status", "/docs", "/terms", "/privacy", "/refund", "/cookies", "/brochure", "/inquiry", "/code-trust", "/why-paxley", "/insights"];
  const isFullPageRoute = fullPageRoutes.some(
    (route) => location.pathname === route || location.pathname.startsWith(route + "/")
  );

  // Public routes
  if (isPublicRoute && isFullPageRoute) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/security" element={<Security />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/docs" element={<Documentation />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/brochure" element={<Brochure />} />
        <Route path="/inquiry" element={<Inquiry />} />
        <Route path="/code-trust" element={<CodeTrust />} />
        <Route path="/why-paxley" element={<WhyPaxley />} />
        <Route path="/insights" element={<InsightsIndex />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
      </Routes>
    );
  }

  // Setup/onboarding (requires auth but full page)
  if (location.pathname.startsWith("/setup") || location.pathname.startsWith("/onboarding")) {
    return (
      <ProtectedRoute>
        <Routes>
          <Route path="/setup" element={<Onboarding />} />
          <Route path="/onboarding" element={<Onboarding />} />
        </Routes>
      </ProtectedRoute>
    );
  }

  // Dashboard routes (protected with layout included in pages)
  return (
    <ProtectedRoute>
      <Routes>
        {/* Main dashboard overview */}
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Repositories list */}
        <Route path="/dashboard/repos" element={<ReposList />} />
        
        {/* Repository details */}
        <Route path="/dashboard/repos/:id" element={<RepoDetails />} />
        
        {/* Upload code (no GitHub) */}
        <Route path="/upload" element={<UploadCode />} />

        {/* Product guide */}
        <Route path="/guide" element={<Guide />} />

        {/* API documentation */}
        <Route path="/api-docs" element={<ApiDocs />} />

        {/* Application assurance workspace */}
        <Route path="/assurance" element={<AssuranceWorkspace />} />

        {/* Legacy redirect */}
        <Route
          path="/repos/:id"
          element={
            <Navigate
              to={location.pathname.replace("/repos/", "/dashboard/repos/")}
              replace
            />
          }
        />
        
        {/* Billing */}
        <Route path="/billing" element={<Billing />} />

        {/* Subscription Management */}
        <Route path="/subscription-management" element={<SubscriptionManagement />} />

        {/* Settings */}
        <Route path="/settings" element={<Settings />} />
        
        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ProtectedRoute>
  );
}

